import React, { ChangeEvent } from 'react';
import { SelectEventArgs, TabComponent } from '@syncfusion/ej2-react-navigations';
import FormFieldsSection, { FormFieldsSectionProps } from './FormFieldsSection';
import ImportMappingSection, { ImportMappingSectionProps } from './ImportMappingSection';
import ValidationSettingsSection, { ValidationSettingsSectionProps } from './ValidationSettingsSection';
import { IConfigurationTreeNode } from './types';
import UrlUtilities from '@shared/common/UrlUtilities';
import ConfigurationField from './ConfigurationField';
import ExternalAttributesPanel, { ExternalAttributesPanelProps } from './externalAttributes/ExternalAttributesPanel';

declare var InovxLoader: any;

export interface AssetClassDetailsSectionProps {
    node: IConfigurationTreeNode;
    configuration: IAssetClassUpdateDetail | null;
    selectedAssetClassTab: number;
    onSelectedAssetClassTabChange: (index: number) => void;
    formFieldsSection: FormFieldsSectionProps;
    externalAttributesSection: ExternalAttributesPanelProps;
    validationSettingsSection: ValidationSettingsSectionProps;
    importMappingSection: ImportMappingSectionProps;
    editableAssetClassName: string;
    hasAssetClassNameError: boolean;
    onAssetClassNameChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

const AssetClassDetailsSection: React.FC<AssetClassDetailsSectionProps> = ({
    node,
    configuration,
    selectedAssetClassTab,
    onSelectedAssetClassTabChange,
    formFieldsSection,
    externalAttributesSection,
    validationSettingsSection,
    importMappingSection,
    editableAssetClassName,
    hasAssetClassNameError,
    onAssetClassNameChange
}) => {
    if (!configuration || configuration.ID !== node.AssetClassID) {
        return <div className="configuration-form-empty-state">{InovxLoader.t('facility:form.saving')}</div>;
    }
    const isExtension = node.NodeType === 'assetClassExtension';
    const isAnnotationClass = node.NodeType === 'annotationClass';
    const iconFileName = isAnnotationClass ? 'AnnotationClass.png' : (isExtension ? 'ClassExtension2.png' : 'AssetClass2.png');
    const iconUrl = `${UrlUtilities.getBaseApplicationUrl()}/images/schema/${iconFileName}`;
    const assetClassTabKey = `${node.TreeNodeKey}:${isExtension ? 'extension' : 'asset-class'}`;
    const assetClassTabCount = isExtension ? 2 : 4;
    const safeSelectedAssetClassTab = Math.min(Math.max(selectedAssetClassTab, 0), assetClassTabCount - 1);
    return (
        <div className="configuration-form-details configuration-form-asset-class-details">
            <div className="row d-flex">
                <div className="col-md-12 field-col" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <img src={iconUrl} alt={node.NodeType} style={{ width: 28, height: 28 }} />
                <div>
                    <div className="configuration-form-title">{`${InovxLoader.t(isAnnotationClass ? 'facility:form.annotationClass' : (isExtension ? 'facility:form.assetClassExtension' : 'facility:form.assetClass'))} - `}<span className="text-bold">{node.Name || configuration.Name}</span></div>
                </div>
                </div>
            </div>
            {!isExtension && (
                <div className="row d-flex">
                    <div className="col-md-12 field-col" style={{marginTop: '4px'}}>
                        <ConfigurationField title={InovxLoader.t('facility:form.className')} inputName="SelectedAssetClassName" className={hasAssetClassNameError ? 'has-error' : ''}>
                            <input
                                className="form-control responsive-control"
                                value={editableAssetClassName}
                                onChange={onAssetClassNameChange}
                                name="SelectedAssetClassName"
                                placeholder={InovxLoader.t('facility:form.assetClassNamePlaceholder')}
                                autoComplete="off"
                            />
                        </ConfigurationField>
                    </div>
                </div>
            )}
            {isExtension ? (
                <TabComponent key={assetClassTabKey} selectedItem={safeSelectedAssetClassTab} selected={(event: SelectEventArgs) => onSelectedAssetClassTabChange(event.selectedIndex)}>
                    <div className="e-tab-header">
                        <div>{InovxLoader.t('facility:form.attributes')}</div>
                        <div>{InovxLoader.t('facility:externalAttributes.tabTitle')}</div>
                    </div>
                    <div className="e-content">
                        <div><FormFieldsSection {...formFieldsSection} /></div>
                        <div><ExternalAttributesPanel {...externalAttributesSection} /></div>
                    </div>
                </TabComponent>
            ) : isAnnotationClass ? (
                <div className="e-control e-tab e-lib e-template e-keyboard" aria-disabled="false" style={{width: '100%', height: 'auto'}}>
                    <div className="e-tab-header e-control e-toolbar e-lib e-keyboard" role="tablist" aria-disabled="false"
                        aria-orientation="horizontal" style={{height: 'auto', width: '100%'}}>
                        <div className="e-items e-toolbar-items" id="_annotation_tab_header_items">
                            <div className="e-indicator e-ignore" style={{left: '0px', right: '0px'}}></div>
                            <div className="e-item e-toolbar-item e-active" id="e-item-annotation_0">
                                <div className="e-tab-wrap" role="tab" aria-selected="true" aria-controls="e-content-annotation_0"
                                    aria-disabled="false">
                                    <div className="e-text-wrap"><span className="e-tab-text" role="presentation">
                                            <div>{InovxLoader.t('facility:form.tagDisplay')}</div>
                                        </span><span className="e-icons e-close-icon" title={InovxLoader.t('common:header.close')}></span></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="e-content e-lib e-touch" style={{height: 'auto'}} id="e-content-annotation_0" role="tabpanel" aria-labelledby="e-item-annotation_0">
                        <div className="e-item e-active" role="tabpanel">
                            <ValidationSettingsSection {...validationSettingsSection} />
                        </div>
                    </div>
                </div>
            ) : (
                <TabComponent key={assetClassTabKey} selectedItem={safeSelectedAssetClassTab} selected={(event: SelectEventArgs) => onSelectedAssetClassTabChange(event.selectedIndex)}>
                    <div className="e-tab-header">
                        <div>{InovxLoader.t('facility:form.attributes')}</div>
                        <div>{InovxLoader.t('facility:externalAttributes.tabTitle')}</div>
                        <div>{InovxLoader.t('facility:form.tagDisplay')}</div>
                        <div>{InovxLoader.t('facility:form.importMapping')}</div>
                    </div>
                    <div className="e-content">
                        <div><FormFieldsSection {...formFieldsSection} /></div>
                        <div><ExternalAttributesPanel {...externalAttributesSection} /></div>
                        <div><ValidationSettingsSection {...validationSettingsSection} /></div>
                        <div><ImportMappingSection {...importMappingSection} /></div>
                    </div>
                </TabComponent>
            )}
        </div>
    );
};

export default AssetClassDetailsSection;
